import React, { useEffect } from 'react';
import { intl } from '@fullstory/intl';
import { FormattedMessage } from 'react-intl';
import { DualMeasure, DualMeasureDimensionApplication } from '@fullstory/proto/fs/api/metrics';
import { Box, Flex, Modal, Pane, SelectMenu, Text } from '@fullstory/components';

const options = [
  {
    label: intl.formatMessage({ defaultMessage: 'Apply to all', id: 'eEmbvT' }),
    key: 'all',
    value: DualMeasureDimensionApplication.DIMENSION_APPLICATION_ALL,
  },
  {
    label: intl.formatMessage({ defaultMessage: 'Apply to first', id: 'Cfy30j' }),
    key: 'first',
    value: DualMeasureDimensionApplication.DIMENSION_APPLICATION_FIRST,
  },
];

export const AdvancedDimensionOptionsModal: React.FC<
  Pick<React.ComponentProps<typeof Modal>, 'onCloseSignal'> & {
    value: DualMeasure;
    onChange: (value: DualMeasure) => void;
    open?: boolean;
  }
> = ({ value, onChange, onCloseSignal, open }) => {
  const [dimensionApplication, setDimensionApplication] = React.useState(
    value.dimensionApplication,
  );
  // if caller changes values for any reason, we want to keep this modal in sync
  useEffect(() => {
    setDimensionApplication(value.dimensionApplication);
  }, [value.dimensionApplication]);

  return (
    <Modal data-component="AdvancedDimensionOptionsModal" onCloseSignal={onCloseSignal} open={open}>
      <Modal.Header onCloseSignal={onCloseSignal}>
        <FormattedMessage defaultMessage="Advanced Options" id="KxN+uh" />
      </Modal.Header>
      <Modal.Body>
        <Flex column gap={16} alignItems="stretch">
          <Box>
            <Text appearance="bodyMedium">
              <FormattedMessage
                defaultMessage="By default Fullstory applies the group by property to all metric events. This option allows the behavior to be adjusted."
                id="5YYfcO"
              />
            </Text>
          </Box>
          <Pane backgroundColor="secondary" padding={16} borderRadius={6}>
            <Flex column gap={16} alignItems="stretch">
              <Box>
                <Box marginBottom={4}>
                  <Text appearance="bodySmallBold">
                    <FormattedMessage defaultMessage="Apply to all" id="eEmbvT" />
                  </Text>
                </Box>
                <Text appearance="bodySmall">
                  <FormattedMessage
                    defaultMessage="Apply the group by to all of the metric events."
                    id="JPubvF"
                  />
                </Text>
              </Box>
              <Box>
                <Box marginBottom={4}>
                  <Text appearance="bodySmallBold">
                    <FormattedMessage defaultMessage="Apply to first" id="Cfy30j" />
                  </Text>
                </Box>
                <Text appearance="bodySmall">
                  <FormattedMessage
                    defaultMessage="Apply the group by to only the first metric event. This is particularly useful for ratios where you want the denominator to remain the full results not scoped to a specific group."
                    id="8QthgB"
                  />
                </Text>
              </Box>
            </Flex>
          </Pane>
          <Box>
            <SelectMenu
              variant="outline"
              options={options}
              selected={options.find(opt => opt.value === dimensionApplication) ?? options[0]}
              onChange={opt => {
                if (!opt) return;
                setDimensionApplication(opt.value);
              }}
            />
          </Box>
        </Flex>
      </Modal.Body>
      <Modal.FooterActions
        primaryAction={{
          action: () => {
            onChange({ ...value, dimensionApplication });
            onCloseSignal();
          },
          text: intl.formatMessage({
            defaultMessage: 'Apply',
            id: 'gIQxJX',
            description: 'Apply changes and close popup',
          }),
        }}
        secondaryAction={{
          action: onCloseSignal,
          text: intl.formatMessage({
            defaultMessage: 'Cancel',
            id: 'JHJyH+',
            description: 'Cancel and close popup',
          }),
        }}
      />
    </Modal>
  );
};
